/**
 * Reply-quote helpers for partner community chat (mirrors safespace-frontend ConversationScreen).
 */

import { isSharedEventCardMessage } from './sharedEventMessage';

export const getMessageReplyParentId = (message) => {
  if (!message || typeof message !== 'object') {
    return null;
  }

  const raw =
    message.replyToMessageId ??
    message.parentId ??
    message.reply_to_message_id ??
    message.parent_id ??
    null;
  const trimmed = String(raw ?? '').trim();
  return trimmed || null;
};

/**
 * Stream Chat replies are one level deep — only top-level messages can be replied to.
 */
export const canReplyToChatMessage = (message) => {
  if (!message?.id) {
    return false;
  }
  return !getMessageReplyParentId(message);
};

export const findReplyParentMessage = (messages, parentId) => {
  const id = String(parentId ?? '').trim();
  if (!id || !Array.isArray(messages)) {
    return null;
  }

  return messages.find((message) => String(message?.id ?? '').trim() === id) ?? null;
};

/** Build an id → message map for O(1) parent lookups while rendering. */
export const buildChatMessageByIdMap = (messages = []) => {
  const map = new Map();
  if (!Array.isArray(messages)) {
    return map;
  }

  messages.forEach((message) => {
    const id = String(message?.id ?? '').trim();
    if (id) {
      map.set(id, message);
    }
  });

  return map;
};

export const getReplyPreviewText = (message) => {
  if (!message) {
    return 'Message';
  }

  const text = String(message.text ?? '').trim();
  if (text) {
    return text;
  }

  if (isSharedEventCardMessage(message) && message.sharedEventPayload?.title) {
    return String(message.sharedEventPayload.title).trim() || 'Event';
  }

  const attachments = Array.isArray(message.attachments) ? message.attachments : [];
  if (attachments.length === 0) {
    return 'Message';
  }

  const first = attachments[0];
  const type = String(first?.type || first?.mimeType || first?.contentType || '').toLowerCase();
  if (type.includes('image') || first?.image) {
    return 'Photo';
  }
  if (type.includes('video')) {
    return 'Video';
  }
  if (type.includes('audio')) {
    return 'Audio';
  }
  return 'Attachment';
};

export const getReplyAuthorName = (
  parentMessage,
  { currentUserIds, currentUserLabel = 'You' } = {},
) => {
  if (!parentMessage) {
    return 'User';
  }

  const candidateIds = [
    parentMessage.senderUid,
    parentMessage.senderId,
    parentMessage.sender?.uid,
    parentMessage.sender?.id,
  ]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean);

  const isOwn = currentUserIds instanceof Set && candidateIds.some((id) => currentUserIds.has(id));

  if (isOwn) {
    return currentUserLabel;
  }

  return (
    String(parentMessage.author ?? '').trim() ||
    String(parentMessage.sender?.fullName ?? '').trim() ||
    String(parentMessage.sender?.name ?? '').trim() ||
    'User'
  );
};
